// src/hooks/useSignup.js
import { useState } from 'react';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc, setDoc, getDoc, collection, query, where, getDocs, serverTimestamp, getFirestore } from 'firebase/firestore';
import { auth, linkSubmissionToUser } from '../services/firebase';
import { useAuthContext } from './useAuthContext';
import { getFunctions, httpsCallable } from 'firebase/functions';

export const useSignup = () => {
  const [error, setError] = useState(null);
  const [isPending, setIsPending] = useState(false);
  const { dispatch } = useAuthContext();

  const createUserProfile = async (user, submissionId = null, firstName = '', lastName = '') => {
    try {
      console.log(`Creating user profile for new user: ${user.uid}, email: ${user.email}`);
      
      const db = getFirestore();
      
      // Check if there's existing waitlist data for this user's email to preserve the link
      let waitlistSubmissionId = submissionId;
      
      // Try to find waitlist entry by email if not already provided (simplified lookup)
      if (user.email && !submissionId) {
        const waitlistRef = collection(db, "waitlist");
        const emailQuery = query(waitlistRef, where("userInfo.email", "==", user.email));
        const emailQuerySnapshot = await getDocs(emailQuery);
        
        if (!emailQuerySnapshot.empty) {
          const docSnap = emailQuerySnapshot.docs[0];
          waitlistSubmissionId = docSnap.id;
          console.log(`Found waitlist data for email ${user.email}, ID: ${waitlistSubmissionId}`);
          
          // Update the submission with userId if not already set
          const waitlistData = docSnap.data();
          if (!waitlistData.userId) {
            await setDoc(doc(db, "waitlist", waitlistSubmissionId), {
              userId: user.uid,
              linkedAt: serverTimestamp()
            }, { merge: true });
            console.log(`Updated waitlist entry with user ID: ${user.uid}`);
            
            // Create mapping for future lookups
            await setDoc(doc(db, "user_submissions_map", user.uid), {
              submissionId: waitlistSubmissionId,
              linkedAt: serverTimestamp()
            });
            console.log("Created mapping document");
          }
        } else {
          console.log(`No waitlist entry found for email ${user.email}`);
        }
      }
      
      // Create minimal user profile data for new onboarding flow
      const userData = {
        uid: user.uid,
        userId: user.uid,
        email: user.email || null,
        displayName: user.displayName || null,
        photoURL: user.photoURL || null,
        phoneNumber: user.phoneNumber || null,
        // Direct signups through /auth are active users, not waitlist
        status: 'active',
        // Add basicInfo structure for Airtable sync consistency
        basicInfo: {
          firstName: firstName || '',
          lastName: lastName || '',
          email: user.email || null,
          name: user.displayName || `${firstName} ${lastName}`.trim()
        },
        // Reference to waitlist submission if found
        waitlistSubmissionId,
        // NEW FLAG - indicates user needs to complete onboarding
        onboardingComplete: false,
        // Timestamps and metadata
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
        emailVerified: user.emailVerified,
        isAdmin: user.email === 'grey@justburnt.com' || 
                user.email === 'arjan@justburnt.com' || 
                user.email === 'louie@justburnt.com'
      };
      
      // Check if profile already exists
      const profileRef = doc(db, "user_profiles", user.uid);
      const profileSnap = await getDoc(profileRef);
      
      if (!profileSnap.exists()) {
        // Create a document in user_profiles collection
        await setDoc(profileRef, userData);
        console.log(`Created user profile document for ${user.uid}`);
      } else {
        // Profile exists, but ensure it's set to active status for direct signups
        // This handles cases where user was previously on waitlist but is now signing up directly
        const existingData = profileSnap.data();
        await setDoc(profileRef, {
          ...existingData,
          status: 'active', // Force active status for direct signups
          onboardingComplete: false,
          lastLogin: serverTimestamp()
        }, { merge: true });
        console.log(`Updated existing profile for ${user.uid} - set status: active, onboardingComplete: false`);
      }
      
      // Create an empty profile summary document if it doesn't exist
      const profileSummaryRef = doc(db, "user_profile_summaries", user.uid);
      const profileSummarySnap = await getDoc(profileSummaryRef);
      
      if (!profileSummarySnap.exists()) {
        const defaultProfileSummary = {
          userID: user.uid,  // Add explicit userID reference
          profileSummary: "",
          keyHighlights: {
            role: "",
            industries: [],
            strengths: []
          },
          idealRoleCharacteristics: {
            roleScope: "",
            keyMotivators: "",
            industryFocus: []
          },
          keySkills: [],
          painPoints: [],
          potentialFit: "",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        
        await setDoc(profileSummaryRef, defaultProfileSummary);
        console.log(`Created profile summary document for ${user.uid}`);
      }
      
      return { success: true };
    } catch (error) {
      console.error(`Error creating user profile for ${user.uid}:`, error);
      return { success: false, error: error.message };
    }
  };

  const signup = async (email, password, displayName, submissionId = null) => {
    setError(null);
    setIsPending(true);
    
    try {
      // Create user
      const res = await createUserWithEmailAndPassword(auth, email, password);
      
      if (!res) {
        throw new Error('Could not complete signup');
      }
      
      // Add display name to user
      await updateProfile(res.user, { displayName });
      
      // Extract first and last name from display name for Airtable sync
      const nameParts = displayName ? displayName.trim().split(' ') : ['', ''];
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      // Create user profile with name fields for Airtable consistency
      await createUserProfile(res.user, submissionId, firstName, lastName);
      
      // If a submissionId is provided, link it to the new user
      if (submissionId) {
        await linkSubmissionToUser(res.user.uid, submissionId);
      }
      
      // Send welcome email
      try {
        const functions = getFunctions();
        const sendWelcomeEmail = httpsCallable(functions, 'sendWelcomeEmail');
        
        await sendWelcomeEmail({
          email: res.user.email,
          firstName: firstName || 'there'
        });
        
        console.log('Welcome email sent successfully');
      } catch (emailError) {
        console.warn('Failed to send welcome email:', emailError);
        // Don't fail the signup if email fails
      }
      
      // Dispatch login action
      dispatch({ type: 'LOGIN', payload: res.user });
      
      setIsPending(false);
      setError(null);
      
      return { success: true, user: res.user };
    } 
    catch (err) {
      console.error('Signup error:', err.message);
      setError(err.message);
      setIsPending(false);
      return { success: false, error: err.message };
    }
  };

  return { signup, error, isPending };
};